import React from 'react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Avatar, PageHeader } from '../components/ui';

const Settings: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, appUser, isAdmin, signOut } = useAuth();
  const name = appUser?.displayName || user?.displayName || user?.email || '';

  return (
    <div className="space-y-6">
      <PageHeader title={t('settings.title')} />

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400">{t('settings.profile')}</h2>
        <div className="mt-4 flex items-center gap-4">
          <Avatar name={name} photoURL={user?.photoURL} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-gray-900 dark:text-white">{name}</p>
            <p className="truncate text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
            {isAdmin && <span className="badge badge-brand mt-1.5">{t('common.admin')}</span>}
          </div>
          <button type="button" onClick={signOut} className="btn btn-secondary btn-sm">
            {t('auth.signOut')}
          </button>
        </div>
      </section>

      <section className="card divide-y divide-gray-100 dark:divide-gray-800">
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('settings.language')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.languageText')}</p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700" role="group">
            {(['hu', 'en'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLanguage(lang)}
                aria-pressed={language === lang}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  language === lang ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {lang === 'hu' ? 'Magyar' : 'English'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('settings.theme')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.themeText')}</p>
          </div>
          <button type="button" onClick={toggleTheme} className="btn btn-secondary btn-sm self-start">
            {theme === 'dark' ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
            {theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Settings;
